import React, { useEffect, useState } from "react";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useSupport } from "@/hooks/useSupport";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import styles from "./settings.module.css";
import supportStyles from "./Support.module.css";

const MAX_FEEDBACK_LENGTH = 500;

const Support: React.FC = () => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const memberInfo = getMemberInfo();
  const [message, setMessage] = useState("");
  const {
    sendSupportRequest,
    loading,
    error,
    success,
    resetSuccess,
    resetError,
  } = useSupport();

  const isSendDisabled =
    !message.trim() || message.length > MAX_FEEDBACK_LENGTH || loading;

  const handleTextAreaFocus = () => {
    document.body.classList.add("cta-hidden");
  };

  const handleTextAreaBlur = () => {
    document.body.classList.remove("cta-hidden");
  };

  const handleSend = async () => {
    if (isSendDisabled) return;
    await sendSupportRequest({ request: message });
  };

  // Show toast on success or error and reset states to prevent infinite loop
  useEffect(() => {
    if (success) {
      showToast({
        message: "Thank you for your feedback!",
        variant: "success",
      });
      setMessage("");
      resetSuccess();
    }
    if (error) {
      showToast({ message: error, variant: "error" });
      resetError();
    }
  }, [success, error, showToast, resetSuccess, resetError]);

  const getDescription = () => {
    return (
      <span>
        {t("support.description")}{" "}
        <a
          href="mailto:support@maatra.health"
          target="_blank"
          className={styles.emailLink}
        >
          support@maatra.health
        </a>
        .
      </span>
    );
  };

  const contactBox = (
    <div className={supportStyles.contactBox}>
      <span className={supportStyles.contactBoxTitle}>
        {t("support.contactBoxTitle")}
      </span>
      <div className={supportStyles.contactRows}>
        <div className={supportStyles.contactRow}>
          <span className={supportStyles.contactLabel}>
            {t("support.whatsapp")}
          </span>
          <a
            href="https://wa.me/919870504540"
            target="_blank"
            rel="noreferrer"
            className={styles.emailLink}
          >
            +91 9870504540
          </a>
        </div>
        <div className={supportStyles.contactRow}>
          <span className={supportStyles.contactLabel}>
            {t("support.email")}
          </span>
          <a
            href="mailto:support@maatra.health"
            target="_blank"
            rel="noreferrer"
            className={styles.emailLink}
          >
            support@maatra.health
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader
        module="support"
        memberInfo={memberInfo}
        description={getDescription()}
      />
      <MSpacer mt={5} />
      <div className="flex-1">
        {memberInfo.email ? (
          <>
            <MTextArea
              id="support-message"
              placeholder={t("support.placeholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MAX_FEEDBACK_LENGTH}
              disabled={loading}
              disableAutoScrollIntoView
              autoGrow
              onFocus={handleTextAreaFocus}
              onBlur={handleTextAreaBlur}
            />
            <div className={supportStyles.contactBoxWrapper}>{contactBox}</div>
          </>
        ) : (
          <>
            <p className={supportStyles.noEmailMessage}>
              {t("support.noEmail")}
            </p>
            <div className={supportStyles.contactBoxWrapper}>{contactBox}</div>
          </>
        )}
      </div>
      {memberInfo.email && (
        <div className="confirm-footer">
          <MButton
            text={t("support.send")}
            variant="filled"
            size="lg"
            disabled={isSendDisabled}
            onClick={handleSend}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default Support;
